import { ContributorStatusType } from '../../common/types';
import {
  convertInviteDTOToInviteData,
  convertInvitesDTOToInvitesData,
  convertTeamMembersDTOToTeamMembersData,
  validateTeamMembersDTO,
} from '../../services/convertTeamMembersDTOToTeamMembersData';
import {
  InviteDTO,
  IsAppInstalled,
  TeamMembersDTO,
  UserDTO,
  UsersMembersDTO
} from '../../services/server-types';
import { showRoleUpdatedToast } from '../../services/showRoleUpdatedToast';
import { Role, RolesArea, RolesData } from '../../types/roles';
import { InviteData, TeamData, TeamMembersData, UserData } from '../../types/teamMembers';
import { ToastName } from '../../types/ToastNames';
import { TeamDetailsState, TeamsServices } from '../configure-store';
import { getAccountId, getIsSite, getMsid } from '../selectors/config';
import {
  getContributorEmailById,
  getContributorStatus,
  getNumberOfUserPages,
  getInviteById,
  getTeamById,
  getUserById,
  getUsersLimit,
} from '../selectors/teamMembers';
import {
  CANCEL_TRANSFER_OWNERSHIP_SUCCESS,
  CancelTransferOwnershipSuccessAction,
  FETCH_AREAS_SUCCESS,
  FETCH_ROLES_SUCCESS,
  FETCH_TEAM_MEMBERS_SUCCESS,
  FETCH_USER_DETAILS_START,
  FETCH_USER_DETAILS_SUCCESS,
  FetchAreasSuccessAction,
  FetchRolesSuccessAction,
  FetchTeamMembersSuccessAction,
  FetchUserDetailsStartAction,
  FetchUserDetailsSuccessAction,
  NEW_INVITES_SENT_SUCCESS,
  NewInvitesSentSuccessAction,
  REMOVE_CONTRIBUTOR_FAILED,
  REMOVE_CONTRIBUTOR_SUCCESS,
  REMOVE_ROLE_SUCCESS,
  RemoveContributorFailedAction,
  RemoveContributorSuccessAction,
  RemoveRoleSuccessAction,
  RESEND_INVITE_SUCCESS,
  REVOKE_INVITE_FAILED,
  REVOKE_INVITE_SUCCESS,
  RevokeInviteFailedAction,
  RevokeInviteSuccessAction,
  SET_INVITE_ROLE_SUCCESS,
  SET_ROLE_AREAS,
  SET_ROLE_SUCCESS,
  SET_TEAM_DETAILS_START,
  SET_TEAM_DETAILS_SUCCESS,
  SET_TEAM_ROLE_SUCCESS,
  SET_TEAM_MEMBER_ROLES_FAILED,
  SET_USER_ROLE_SUCCESS,
  SetRoleAreasAction,
  SetRoleSuccessAction,
  SetTeamDetailsStartAction,
  SetTeamDetailsSuccessAction,
  SetTeamMemberRolesFailedAction,
  SetContributorLimitActionAction,
  SET_CONTRIBUTOR_LIMIT,
  SET_SHOULD_ENFORCE_CONTRIBUTORS,
  SetShouldEnforceContributorsAction,
  SET_LOADING_TEAMMATES,
  SetLoadingTeammatesAction,
} from './action-types';
import { showToast } from './activeToast.actions';
import {
  reportCreateCustomRole,
  reportDeleteRoleSuccess,
  reportUpdateCustomRole,
  reportSaveTeamDetails,
  reportInvitePeopleClick,
} from './bi.actions';
import { Area } from '../../types/areas';
import {
  convertPolicyToRoleData,
  convertRolesDTOToRolesData,
  convertScopesToStatements
} from '../../services/convertRolesDTOToRolesData';
import { createAreas } from '../../services/areasService';
import { getAllScopes } from '../selectors/areas';
import { getRoleTitle, getRoleTitles } from '../selectors/roles';
import { createRoleAreas } from '../../services/roleService';
import { ServerApi } from '../../services/server-api';
import { ModalName } from '../../types/ModalNames';
import { showModal } from './activeModal.actions';
import { captureException } from '../../services/sentry-client';
import { moveToInvitePage } from '../../services/historyService';
import {
  getIsEnforceContributorsEnabled, getIsEnforceContributorsFreePlan, getContributorLimit,
  getShouldUpgradeBeforeInviteContributors
} from '../selectors/teamDetails';
import { predefinedRoles } from '../../rolesConfig/predefinedRolesConfig';

const fetchTeamMembersSuccess = (roles: TeamMembersData): FetchTeamMembersSuccessAction => {
  return ({
    type: FETCH_TEAM_MEMBERS_SUCCESS,
    roles,
  });
};

const fetchRolesSuccess = (roles: RolesData): FetchRolesSuccessAction => {
  return ({
    type: FETCH_ROLES_SUCCESS,
    roles,
  });
};

const setRoleAreas = (roleAreas: RolesArea[]): SetRoleAreasAction => {
  return ({
    type: SET_ROLE_AREAS,
    roleAreas,
  });
};

const setLoadingTeammates = (loadingTeammates: boolean): SetLoadingTeammatesAction => {
  return ({
    type: SET_LOADING_TEAMMATES,
    loadingTeammates,
  });
};

export function fetchCurrentUser() {
  return (dispatch, getState, { serverApi }: TeamsServices) => {
    dispatch(fetchCurrentUserDetailsStart());
    serverApi.fetchCurrentUserDetails()
      .then(currentUser => dispatch(fetchCurrentUserDetailsSuccess(currentUser)))
      .catch(e => dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message })));
  };
}

export function fetchTeamMembers(shouldUseServerPredefinedConfigFile: boolean) {
  return async (dispatch, getState, { serverApi }: TeamsServices) => {
    try {
      const usersLimit = getUsersLimit(getState());
      const teamMembersDTO = await serverApi.fetchTeamMembers(usersLimit);
      dispatch(saveTeamMembersDataToRedux(teamMembersDTO));
      dispatch(savePoliciesDataToRedux(teamMembersDTO, shouldUseServerPredefinedConfigFile));
      return teamMembersDTO;
    } catch (e) {
      dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message }));
    }
  };
}

export function fetchTeamMemberUsers(prevTeamMembersDTO: TeamMembersDTO) {
  return (dispatch, getState, { serverApi }: TeamsServices) => {
    const isSite = getIsSite(getState());
    if (!isSite) {
      try {
        dispatch(fetchMoreTeamMemberUsersIfNeeded(prevTeamMembersDTO));
      } catch (e) {
        dispatch(setLoadingTeammates(false));
        dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message }));
      }
    }
  };
}

export function sendInvitesV2(emails: string[], roleIds: string[]) {
  return async (dispatch, getState, { serverApi }: TeamsServices) => {
    try {
      const res = await serverApi.inviteContributorsWithPolicyIds(emails, roleIds, getAccountId(getState()));
      const invites = convertInvitesDTOToInvitesData(res.invites);
      dispatch(newInvitesSentSuccess(invites));
    } catch (e) {
      dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message }));
    }
  };
}

export function setTeamDetails(teamDetails: TeamDetailsState, isShowToast: boolean = false) {
  return (dispatch, getState, { serverApi }: TeamsServices) => {
    const state = getState();
    const { name, imageUrl } = teamDetails;
    dispatch(setTeamDetailsStart());
    const accountId = getAccountId(state);
    return serverApi.setTeamDetails(accountId, name, imageUrl).then(() => {
      dispatch(setTeamDetailsSuccess(teamDetails));
      isShowToast && dispatch(showToast(ToastName.UpdateTeamDetailsSuccess));
      dispatch(reportSaveTeamDetails(!isShowToast));
    }).catch(e => dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message })));
  };
}

export const setTeamMemberRolesV2 = (entity, roleIds: string[]) => (dispatch, getState, { serverApi }: TeamsServices) => {
  const id = entity.inviteId || entity.userId || entity.accountId;
  const prevRoleIds = entity.roles;
  const userStatus = getContributorStatus(getState(), id);
  const roleTitles = getRoleTitles(getState(), roleIds);
  let returnPromise;
  if (userStatus === ContributorStatusType.USER) {
    dispatch(setUserRoleSuccess(id, roleIds));
    returnPromise = serverApi.setContributorDetails(id, roleIds)
      .then(() => {
        showRoleUpdatedToast(dispatch, entity, roleTitles);
      });
  } else if (userStatus === ContributorStatusType.INVITE) {
    dispatch(setInviteRoleSuccess(id, roleIds));
    returnPromise = serverApi.setInviteDetails(id, roleIds).then(() => {
      showRoleUpdatedToast(dispatch, entity, roleTitles);
    });
  } else if (userStatus === ContributorStatusType.TEAM) {
    dispatch(setTeamRoleSuccess(id, roleIds));
    returnPromise = serverApi.setContributorDetails(id, roleIds).then(() => {
      showRoleUpdatedToast(dispatch, entity, roleTitles);
    });
  }
  returnPromise.catch(e => {
    dispatch(setTeamMemberRoleFailed(id, prevRoleIds));
    dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message }));
    captureException(e, getIsSite(getState()));
  });
  return returnPromise;
};

export const removeFromTeam = (id: string, emailToRemove: string) => (dispatch, getState) => {
  const userStatus = getContributorStatus(getState(), id);

  if (userStatus === ContributorStatusType.INVITE) {
    dispatch(revokeInvite(id, emailToRemove));
  } else {
    dispatch(removeUser(id, emailToRemove));
  }
};

const removeUser = (id: string, emailToRemove: string) => (dispatch, getState, { serverApi }: TeamsServices) => {
  const user = getUserById(getState(), id);
  const team = getTeamById(getState(), id);
  dispatch(removeContributorSuccess(id));
  return serverApi.removeContributor(id)
    .then(() => {
      dispatch(showToast(ToastName.RemoveMembersSuccess, { translationParams: { email: emailToRemove } }));
    })
    .catch(e => {
      dispatch(removeContributorFailed(user, team));
      dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message }));
    });
};

const revokeInvite = (id: string, emailToRemove: string) => (dispatch, getState, { serverApi }: TeamsServices) => {
  const invite = getInviteById(getState(), id);
  dispatch(revokeInviteSuccess(id));
  return serverApi.revokeInvite(id)
    .then(() => {
      dispatch(showToast(ToastName.RemoveMembersSuccess, { translationParams: { email: emailToRemove } }));
    })
    .catch(e => {
      dispatch(revokeInviteFailed(invite));
      dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message }));
      captureException(e, getIsSite(getState()));
    });
};

export const resendInvite = id => (dispatch, getState, { serverApi }: TeamsServices) => {
  serverApi
    .resendInvite(id)
    .then((updatedInviteDto: InviteDTO) => {
      const updatedInviteData = convertInviteDTOToInviteData(updatedInviteDto);
      const email = getContributorEmailById(getState(), id);
      dispatch(resendInviteSuccess(id, updatedInviteData));
      dispatch(showToast(ToastName.InviteResendSuccess, { translationParams: { email } }));
    })
    .catch(e => dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message })));
};

export const fetchAreas = () => (dispatch, getState, { serverApi }: TeamsServices) => {
  const areas = createAreas({ msid: getState().msid, isAppInstalled: serverApi.isAppInstalled });
  return dispatch(fetchAreasSuccess(areas));
};

export const setRole = (role, shouldUpdateRoleStatements, origin: string, isDuplicate?: boolean, shouldSendCondition?: boolean) => async (dispatch, getState, { serverApi }: TeamsServices) => {
  try {
    const newPolicy = {
      name: role.title,
      description: role.description,
      statements: convertScopesToStatements(getAllScopes(getState()), role.scopes, shouldSendCondition),
    };
    if (role.id) { //Update Role
      const [metadataRes, statementsRes] = await Promise.all([
        serverApi.updateRoleMeta({ id: role.id, name: role.title, description: role.description }),
        shouldUpdateRoleStatements && serverApi.updateRoleStatements({ statements: newPolicy.statements, id: role.id })
      ]);
      let updatedPolicy = metadataRes.policy;
      if (statementsRes) {
        updatedPolicy.statements = statementsRes.policy?.statements;
      }
      const roleData = convertPolicyToRoleData(updatedPolicy);
      dispatch(showToast(ToastName.UpdateRoleSuccess, { translationParams: { roleTitle: role.title } }));
      dispatch(reportUpdateCustomRole(role.scopes.length, role.id, role.scopes));
      dispatch(setRoleSuccess(roleData));
      return role.id;
    } else { //New Role
      const { policy } = await serverApi.createRole(newPolicy); //should add error handling
      const roleData = convertPolicyToRoleData(policy);
      dispatch(showToast(ToastName.SetRoleSuccess, { translationParams: { roleTitle: role.title } }));
      dispatch(reportCreateCustomRole(role.scopes.length, origin, role.scopes, roleData.id, isDuplicate));
      dispatch(setRoleSuccess(roleData));
      return roleData.id;
    }
  } catch (e) {
    dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message }));
  }
};

export const removeRole = (roleId) => (dispatch, getState, { serverApi }: TeamsServices) => {
  const isSite = getIsSite(getState());
  const roleTitle = getRoleTitle(getState(), roleId);
  return serverApi.removeRole(roleId).then(() => {
    dispatch(showToast(ToastName.RemoveRoleSuccess, { translationParams: { roleTitle } }));
    dispatch(reportDeleteRoleSuccess(roleId));
    return dispatch(removeRoleSuccess(roleId));
  }).catch(e => {
    let errorData = e?.response?.data;
    let title = '';
    let errorMessage = errorData?.message;
    if (isSite) {
      title = 'modal.errorModal.removeRoleTitle';
      errorMessage = 'modal.errorModal.siteRemoveRoleErrorMessage';
    } else if (errorData?.details?.applicationError?.code === '-1806')  {
      title = 'modal.errorModal.removeRoleTitle';
      errorMessage = 'modal.errorModal.partnersRemoveRoleErrorMessage';
    }
    dispatch(showModal(ModalName.Error, {
      title,
      errorMessage,
    }));
  });
};

export const resendTransferOwnershipEmail = () => (dispatch, getState, { serverApi }: TeamsServices) => {
  return serverApi.resendTransferOwnershipEmail().then(() => {
    return dispatch(showToast(ToastName.ResendTransferOwnership));
  }).catch(e => dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message })));
};

export const cancelTransferOwnership = () => (dispatch, getState, { serverApi }: TeamsServices) => {
  return serverApi.cancelTransferOwnership().then(() => {
    dispatch(showToast(ToastName.CancelTransferOwnership));
    return dispatch(cancelTransferOwnershipSuccess());
  }).catch(e => dispatch(showModal(ModalName.Error, { errorMessage: e?.response?.data?.message })));
};

export const setEnforceContributors = (shouldEnforceContributors: boolean) => (dispatch, getState, { serverApi }: TeamsServices) => {
  dispatch(setShouldEnforceContributors(shouldEnforceContributors));
};

export const getEnforceContributors = (params) => async (dispatch, getState, { serverApi }: TeamsServices) => {
  const isEnforceContributorsEnabled = getIsEnforceContributorsEnabled(getState());
  let shouldUpgradeToInvite = false;

  if (!isEnforceContributorsEnabled) {
    moveToInvitePage(params);
  } else { //protected under FT
    const contributorLimitRes = await serverApi.fetchContributorLimit();
    const contributorLimit = contributorLimitRes?.contributorLimitation?.contributorLimit;
    dispatch(setContributorLimit(contributorLimit));
    if (!dispatch(showEnforceContributorsUpgradeModalIfNecessary(params.origin, contributorLimit))) {
      moveToInvitePage(params);
    } else {
      shouldUpgradeToInvite = true;
    }
  }
  dispatch(reportInvitePeopleClick(params.origin, shouldUpgradeToInvite));
};

export const showEnforceContributorsUpgradeModalIfNecessary = (origin: string, contributorLimit?: number) => (dispatch, getState, { serverApi }: TeamsServices): boolean => {
  const shouldUpgrade = getShouldUpgradeBeforeInviteContributors(getState(), contributorLimit);
  if (shouldUpgrade) {
    dispatch(showModal(ModalName.EnforceContributorsUpgrade,
      {
        shouldRedirectToMainPageOnClose: !contributorLimit,
        msid: getMsid(getState()),
        contributorLimit: getContributorLimit(getState()),
        isFreePlan: getIsEnforceContributorsFreePlan(getState()),
        origin
      }));
    return true;
  } else {
    return false;
  }
};

const resendInviteSuccess = (id: string, updatedInviteData: InviteData) => {
  return {
    type: RESEND_INVITE_SUCCESS,
    updatedInviteData
  };
};

const newInvitesSentSuccess = (
  newInvites: InviteData[]
): NewInvitesSentSuccessAction => {
  return {
    type: NEW_INVITES_SENT_SUCCESS,
    newInvites,
  };
};

const removeContributorSuccess = (id: string): RemoveContributorSuccessAction => {
  return {
    type: REMOVE_CONTRIBUTOR_SUCCESS,
    removedContributorId: id,
  };
};

const removeContributorFailed = (user: UserData, team: TeamData): RemoveContributorFailedAction => {
  return {
    type: REMOVE_CONTRIBUTOR_FAILED,
    user,
    team,
  };
};

const revokeInviteSuccess = (inviteId: string): RevokeInviteSuccessAction => {
  return {
    type: REVOKE_INVITE_SUCCESS,
    revokedInviteId: inviteId,
  };
};

const revokeInviteFailed = (invite: InviteData): RevokeInviteFailedAction => {
  return {
    type: REVOKE_INVITE_FAILED,
    invite,
  };
};

const setTeamDetailsStart = (): SetTeamDetailsStartAction => {
  return {
    type: SET_TEAM_DETAILS_START,
  };
};

const setTeamDetailsSuccess = (teamDetails: TeamDetailsState): SetTeamDetailsSuccessAction => {
  return {
    type: SET_TEAM_DETAILS_SUCCESS,
    teamDetails,
  };
};

const setUserRoleSuccess = (userId, roles: string[]) => {
  return {
    type: SET_USER_ROLE_SUCCESS,
    userId,
    roles,
  };
};

const setInviteRoleSuccess = (inviteId: string, roles: string[]) => {
  return {
    type: SET_INVITE_ROLE_SUCCESS,
    inviteId,
    roles,
  };
};

const setTeamRoleSuccess = (accountId: string, roles: string[]) => {
  return {
    type: SET_TEAM_ROLE_SUCCESS,
    accountId,
    roles,
  };
};

const setTeamMemberRoleFailed = (teamMemberId: string, roles: string[]): SetTeamMemberRolesFailedAction => {
  return {
    type: SET_TEAM_MEMBER_ROLES_FAILED,
    teamMemberId,
    roles,
  };
};

const fetchCurrentUserDetailsStart = (): FetchUserDetailsStartAction => {
  return {
    type: FETCH_USER_DETAILS_START,
  };
};

const fetchCurrentUserDetailsSuccess = (
  userDetails
): FetchUserDetailsSuccessAction => {
  return {
    type: FETCH_USER_DETAILS_SUCCESS,
    userDetails,
  };
};

const fetchAreasSuccess = (areas: Area[]): FetchAreasSuccessAction => {
  return {
    type: FETCH_AREAS_SUCCESS,
    areas,
  };
};

const setRoleSuccess = (role: Role): SetRoleSuccessAction => {
  return {
    type: SET_ROLE_SUCCESS,
    role,
  };
};

const removeRoleSuccess = (roleId: string): RemoveRoleSuccessAction => {
  return {
    type: REMOVE_ROLE_SUCCESS,
    roleId,
  };
};

const cancelTransferOwnershipSuccess = (): CancelTransferOwnershipSuccessAction => {
  return {
    type: CANCEL_TRANSFER_OWNERSHIP_SUCCESS,
  };
};

const setContributorLimit = (contributorLimit: number): SetContributorLimitActionAction => {
  return {
    type: SET_CONTRIBUTOR_LIMIT,
    contributorLimit,
  };
};

const setShouldEnforceContributors = (shouldEnforceContributors: boolean): SetShouldEnforceContributorsAction => {
  return {
    type: SET_SHOULD_ENFORCE_CONTRIBUTORS,
    shouldEnforceContributors,
  };
};

const convertTeamMembersDTO = (teamMembersDTO: TeamMembersDTO) => {
  const validatedTeamMembers = validateTeamMembersDTO(teamMembersDTO);
  const teamMembersData: TeamMembersData = convertTeamMembersDTOToTeamMembersData(validatedTeamMembers);
  return teamMembersData;
};

const convertPoliciesDTO = (teamMembersDTO: TeamMembersDTO, isSite: boolean, isAppInstalled: IsAppInstalled) => {
  let rolesData;
  let roleAreas;
  if (teamMembersDTO.policies) {
    rolesData = convertRolesDTOToRolesData(teamMembersDTO.policies, isSite, isAppInstalled, teamMembersDTO.predefinedRoles?.roles);
    roleAreas = createRoleAreas(rolesData, teamMembersDTO.predefinedRoles?.roles);
  }
  return { rolesData, roleAreas };
};

const saveTeamMembersDataToRedux = (teamMembersDTO: TeamMembersDTO) => (dispatch) => {
  const teamMembersData = convertTeamMembersDTO(teamMembersDTO);
  dispatch(fetchTeamMembersSuccess(teamMembersData));
};

const savePoliciesDataToRedux = (teamMembersDTO: TeamMembersDTO, shouldUseServerPredefinedConfigFile: boolean) => (dispatch, getState, { serverApi }: TeamsServices) => {
  if (teamMembersDTO.policies) {
    if (!shouldUseServerPredefinedConfigFile) {
      teamMembersDTO.predefinedRoles = {
        roles: predefinedRoles
      };
    }
    const { rolesData, roleAreas } = convertPoliciesDTO(teamMembersDTO, getIsSite(getState()), serverApi.isAppInstalled);
    dispatch(fetchRolesSuccess(rolesData));
    dispatch(setRoleAreas(roleAreas));
  }
};

const fetchMoreTeamMemberUsersIfNeeded = (teamMembersDTO: TeamMembersDTO) => (dispatch, getState, { serverApi }: TeamsServices) => {
  let users = teamMembersDTO?.users;
  let usersFetchPromises;
  const usersLimit = getUsersLimit(getState());
  const userPages = getNumberOfUserPages(usersLimit, teamMembersDTO?.totalUsersInAccount);
  if (userPages > -1) {
    dispatch(setLoadingTeammates(true));
    usersFetchPromises = new Array(userPages).fill('').map((_, prevPageNum) => {
      return fetchTeamMemberUserPage(serverApi, usersLimit, prevPageNum + 1)
        .then(usersPage => {
          users.push(...usersPage);
          dispatch(saveTeamMembersDataToRedux(teamMembersDTO));
        });
    });
  } else if (users.length === usersLimit) {
    dispatch(setLoadingTeammates(true));
    let pageNum = 1;
    usersFetchPromises = [dispatch(fetchMoreTeamMemberUsersFallback(users, pageNum, usersLimit, teamMembersDTO))];
  }
  if (usersFetchPromises) {
    Promise.all(usersFetchPromises).then(_ => dispatch(setLoadingTeammates(false)));
  }
};

const fetchMoreTeamMemberUsersFallback = (users: UserDTO[], pageNum: number, usersLimit: number, teamMembersDTO: TeamMembersDTO) => (dispatch, getState, { serverApi }: TeamsServices) => {
  return fetchTeamMemberUserPage(serverApi, usersLimit, pageNum++).then(newUsers => {
    users.push(...newUsers);
    dispatch(saveTeamMembersDataToRedux(teamMembersDTO));
    if (newUsers?.length === usersLimit) {
      return dispatch(fetchMoreTeamMemberUsersFallback(users, pageNum, usersLimit, teamMembersDTO));
    }
  });
};

  const fetchTeamMemberUserPage = (serverApi: ServerApi, limit: number, pageNum: number = 1) => {
  return serverApi.fetchTeamMemberUsers(limit, pageNum).then(async (usersMembersDTO: UsersMembersDTO) => {
    return usersMembersDTO?.users;
  });
};
